import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../utils/apiClient';
import { toast } from 'react-toastify';
import { investorService } from '../../../services/investor.service';
import { announceJourneySubmitted } from '../../../utils/journeySubmitSignal';
import { nextStepPathAfter, toServiceAgentPath } from '../../../utils/journeySteps';
import './InvestorProfilePdf.css';
import { getActiveInvestorJourney } from '../../../services/investorJourneyScope';
import { profileService, JourneyReadinessDto, InvestorConsentsDto } from '../../../services/profile.service';
import { useDelegationPermissions } from '../../../contexts/DelegationPermissionsContext';
import { getSAProxyInvestorId, saProxyApiClient } from '../../../services/saProxyAdapter';


/** The journey whose booklet/preview is being generated (see investorJourneyScope). */
const journeyParams = (): { journeyId: string } | undefined => {
  // H-20 (2026-08-23): this page opens in a NEW TAB — sessionStorage does not reliably
  // transfer across tabs, so the journey rides in the URL; session scope is the fallback.
  const fromUrl = new URLSearchParams(window.location.search).get('journeyId');
  const jid = fromUrl || getActiveInvestorJourney();
  return jid ? { journeyId: jid } : undefined;
};

export const InvestorProfilePdf: React.FC = () => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  // SA proxy context (default: not proxy). On the SA route this page sits inside
  // ServiceAgentProxyWrapper, so the adapter rewrites its API calls and this context
  // carries the delegation's flags; navigation must then stay on SA routes.
  const delegationPerms = useDelegationPermissions();
  const saInvestorId = getSAProxyInvestorId();
  const journeyHome = saInvestorId !== null
    ? `/service-agent/investors/${saInvestorId}/profile`
    : '/investor/journey';

  useEffect(() => {
    fetchPrintPreview();
  }, []);

  useEffect(() => {
    // Expose functions to window for the FreeMarker template buttons
    (window as any).submitProfile = handleSubmitProfile;
    (window as any).downloadPdf = handleDownloadPdf;
    
    // Cleanup
    return () => {
      delete (window as any).submitProfile;
      delete (window as any).downloadPdf;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This page calls apiClient DIRECTLY (not via a service), which skips the SA proxy
  // rewrite — the exact bug that sent the agent's token to investor endpoints and
  // bounced them to B2C (2026-09-06). Pick the client the way profile.service does.
  const httpClient = getSAProxyInvestorId() !== null ? saProxyApiClient : apiClient;

  const fetchPrintPreview = async () => {
    try {
      setLoading(true);
      const response = await httpClient.get<string>('/api/investor/pdf/print-preview', {
        headers: {
          'Accept': 'text/html'
        },
        responseType: 'text',
        params: journeyParams()
      });
      setHtmlContent(response.data as string);
    } catch (error: any) {
      console.error('Failed to fetch print preview:', error);
      toast.error('Failed to load PDF preview');
    } finally {
      setLoading(false);
    }
  };

  // H-20 Laravel parity (user 2026-08-23): Laravel's preview page submits the final
  // submission DIRECTLY from the PDF. Ours does the same — but through OUR gates,
  // undisturbed: the three declaration consents must be ticked (in the modal below, same
  // wording as the Final Submission tab) and the server-side readiness check still 409s
  // with the missing list. The tab's own flow is untouched.
  const [readiness, setReadiness] = useState<JourneyReadinessDto | null>(null);
  // A6: the downloaded file is named after the investor (Laravel parity).
  const [investorName, setInvestorName] = useState<{ first?: string; last?: string }>({});
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Declarations are ticked on the Final Submit TAB (user 2026-08-26) and ride here as
  // decl=1 — appended by the tab ONLY when all three were ticked. No checkboxes on this
  // page: the modal is a plain are-you-sure prompt. Without decl (direct link, or the tab
  // opened before ticking) the Submit button is hidden — attestation is never implied.
  const declarationsGiven = new URLSearchParams(window.location.search).get('decl') === '1';

  useEffect(() => {
    // C5 fix (2026-08-25): pass the URL-first journey explicitly — this page opens in a
    // new tab where sessionStorage may be empty; profileService's session fallback then
    // pointed readiness (and the SUBMIT below) at the PRIMARY journey, not the previewed one.
    profileService.getFinalSubmitReadiness(journeyParams()?.journeyId)
      .then(setReadiness)
      .catch(() => setReadiness(null)); // unknown → button hidden
    profileService.getPersonalInfo()
      .then((pi) => setInvestorName({ first: pi?.investorFirstName, last: pi?.investorLastName }))
      .catch(() => { /* filename falls back to the generic one */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitProfile = async () => {
    if (!declarationsGiven || submitting) return;
    setSubmitting(true);
    try {
      // All three were ticked on the Final Submit tab (decl=1 is only set in that case).
      const consents: InvestorConsentsDto = {
        informationCorrectConsent: true,
        legalCapacityConsent: true,
        modificationAwarenessConsent: true,
      };
      await profileService.finalSubmit(consents, journeyParams()?.journeyId);
      toast.success('Journey submitted successfully');
      setSubmitOpen(false);
      const jid = journeyParams()?.journeyId ?? null;
      // This tab was opened from the Final-Submit form by Generate Preview. Tell that tab
      // the journey is submitted (it moves on to the journey page) and close this one, so
      // the investor is left with ONE portal tab instead of a stale form plus this preview.
      announceJourneySubmitted({ journeyId: jid, saInvestorId });
      window.close();
      // Only reached when the browser refuses to close (tab not script-opened, e.g. after
      // a refresh): fall back to navigating here. SA tab: the SA information page —
      // investor journey routes would bounce the agent's role at the guard.
      setTimeout(async () => {
        const fresh = await investorService.getDashboard(jid ?? undefined).catch(() => null);
        const target = nextStepPathAfter('information', fresh, jid);
        navigate(saInvestorId !== null ? toServiceAgentPath(target, saInvestorId) : target);
      }, 150);
    } catch (err: any) {
      if (err?.response?.status === 409) {
        const missing: string[] = err.response?.data?.missing ?? [];
        toast.error(missing.length
          ? `Not ready to submit — complete: ${missing.join(', ')}`
          : 'This journey is not ready for final submission yet.');
      } else {
        toast.error(err?.response?.data?.message || 'Failed to submit');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrintPdf = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await httpClient.get<Blob>('/api/investor/pdf/view', {
        responseType: 'blob',
        params: journeyParams()
      });
      
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data as BlobPart]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // A6 (2026-09-07): Laravel emitted Investor_Information_RAVI_SHARMA.pdf — the name makes
      // the file identifiable once downloaded. Falls back to the plain name when unknown.
      const who = [investorName.first, investorName.last]
        .filter((p) => p && p.trim())
        .join('_')
        .replace(/[^A-Za-z0-9_]/g, '')
        .toUpperCase();
      link.setAttribute('download', who ? `Investor_Information_${who}.pdf` : 'investor_information.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error: any) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleClose = () => {
    window.close();
    // If window.close() doesn't work (popup blocker), navigate back
    setTimeout(() => {
      navigate(journeyHome);
    }, 100);
  };

  if (loading) {
    return (
      <div className="pdf-preview-loading">
        <div className="spinner"></div>
        <p>Loading PDF Preview...</p>
      </div>
    );
  }

  return (
    <div className="pdf-preview-container">
      <div className="pdf-preview-toolbar no-print">
        {/* Laravel preview-page parity: Submit lives here; disabled once submitted.
            Same rule as the Final Submit tab: an SP-approved re-edit (editUnlocked) or a
            send-back/rejection (reeditable) reopens the journey, so Submit must work again.
            Shown only when the tab's declarations rode along (decl=1). */}
        {readiness && (readiness.alreadySubmitted && !readiness.editUnlocked && !readiness.reeditable
          ? (
            <button className="pdf-toolbar__btn" disabled title="Already submitted">
              Submitted
            </button>
          ) : delegationPerms.isProxyMode && !delegationPerms.canSubmitForms ? (
            /* Delegation without Submit Forms: viewing/printing is allowed, submitting is
               the investor's (the backend double-gates this anyway — display honesty). */
            <button className="pdf-toolbar__btn" disabled
              title="You don't have permission to submit on behalf of this investor. Contact them to update delegation permissions.">
              Submit
            </button>
          ) : declarationsGiven ? (
            <button onClick={() => setSubmitOpen(true)} className="pdf-toolbar__btn" style={{ background: '#052f55', color: '#fff' }}>
              Submit
            </button>
          ) : (
            <button className="pdf-toolbar__btn" disabled
              title="Tick the three declarations on the Final Submit tab first, then reopen this preview">
              Submit
            </button>
          ))}
        <button onClick={handlePrintPdf} className="pdf-toolbar__btn">
          Print PDF
        </button>
        <button onClick={handleDownloadPdf} className="pdf-toolbar__btn">
          Download PDF
        </button>
        <button onClick={handleClose} className="pdf-toolbar__close">
          Close
        </button>
      </div>

      {submitOpen && (
        <div className="no-print" style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 8, maxWidth: 520, width: '92%', padding: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Are you sure?</h3>
            <p style={{ fontSize: 13, marginBottom: 4 }}>Do you want to submit the form?</p>
            <p style={{ fontSize: 13, color: '#dc3545', fontWeight: 700, marginBottom: 12 }}>
              Details cannot be changed after submission
            </p>
            {/* Declarations were ticked on the Final Submit tab (decl=1 gate) — this is a
                plain confirmation, no repeated checkboxes (user 2026-08-26). */}
            {/* Fully inline-styled: the page injects the server-rendered profile HTML
                (dangerouslySetInnerHTML) whose global styles otherwise mangle these
                buttons — inline wins over any injected stylesheet rule. */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => setSubmitOpen(false)}
                disabled={submitting}
                style={{
                  padding: '8px 18px', fontSize: 14, lineHeight: 1.4, fontFamily: 'inherit',
                  fontWeight: 600, border: '1px solid #cbd5e1', borderRadius: 6,
                  background: '#fff', color: '#0f172a', cursor: 'pointer',
                  whiteSpace: 'nowrap', width: 'auto', height: 'auto', minWidth: 90,
                  margin: 0, boxSizing: 'border-box', textTransform: 'none', letterSpacing: 'normal',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitProfile}
                disabled={submitting}
                style={{
                  padding: '8px 18px', fontSize: 14, lineHeight: 1.4, fontFamily: 'inherit',
                  fontWeight: 600, border: 'none', borderRadius: 6,
                  background: '#052f55', color: '#fff',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap', width: 'auto', height: 'auto', minWidth: 120,
                  margin: 0, boxSizing: 'border-box', textTransform: 'none', letterSpacing: 'normal',
                }}
              >
                {submitting ? 'Submitting…' : 'Yes, submit it!'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div 
        className="pdf-preview-content"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    </div>
  );
};
